/**
 * Question Import Modal
 * Allows admin to upload and import question files
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  X,
  Upload,
  FileJson,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Download,
  RefreshCw
} from 'lucide-react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { createLogger } from '../../utils/logger'

const logger = createLogger('QuestionImportModal')

const QuestionImportModal = ({ isOpen, onClose, onImportComplete }) => {
  const [mode, setMode] = useState('upsert')
  const [validateOnly, setValidateOnly] = useState(false)
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [file, setFile] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [importJob, setImportJob] = useState(null)
  const pollIntervalRef = useRef(null)
  const importIdRef = useRef(null)
  // Tracks the validateOnly value of the CURRENTLY-submitted job (may differ from the checkbox, e.g.
  // a pseudo-replace preview always submits validateOnly=true). Set in submitImport, read by the poll.
  const validateOnlyRef = useRef(false)
  const fileInputRef = useRef(null)

  // Clear state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFile(null)
      setImportJob(null)
      setIsUploading(false)
      setValidateOnly(false)
      setConfirmReplace(false)
    }
  }, [isOpen])

  // Poll for import status
  const pollImportStatus = useCallback(
    async (importId) => {
      try {
        const response = await api.get(`/admin/questions/import/${importId}`)
        const importData = response.data.import

        setImportJob(importData)

        if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(importData.status)) {
          // Stop polling
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
          }
          importIdRef.current = null
          // Re-enable actions (e.g. the pseudo-replace "Apply" button after its preview completes).
          setIsUploading(false)

          if (importData.status === 'COMPLETED') {
            toast.success(
              validateOnlyRef.current
                ? 'Validation completed!'
                : `Import completed! ${importData.successCount} questions imported.`
            )
            if (!validateOnlyRef.current && onImportComplete) {
              onImportComplete()
            }
          } else if (importData.status === 'FAILED') {
            toast.error('Import failed. Check the errors below.')
          }
        }
      } catch (error) {
        logger.error('Failed to poll import status:', error)
      }
    },
    [onImportComplete]
  )

  // Start polling when we have an import ID to track
  const startPolling = useCallback(
    (importId) => {
      // Don't start if already polling this ID
      if (importIdRef.current === importId) return

      // Clear any existing polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }

      importIdRef.current = importId

      // Poll immediately
      pollImportStatus(importId)

      // Then poll every 2 seconds
      pollIntervalRef.current = setInterval(() => {
        pollImportStatus(importId)
      }, 2000)
    },
    [pollImportStatus]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      importIdRef.current = null
    }
  }, [])

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && isValidFile(droppedFile)) {
      setFile(droppedFile)
    } else {
      toast.error('Please upload a JSON or CSV file')
    }
  }

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile && isValidFile(selectedFile)) {
      setFile(selectedFile)
    } else {
      toast.error('Please upload a JSON or CSV file')
    }
  }

  const isValidFile = (file) => {
    const validExtensions = ['.json', '.csv']
    const ext = '.' + file.name.split('.').pop().toLowerCase()
    return validExtensions.includes(ext)
  }

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop().toLowerCase()
    if (ext === 'json') return <FileJson className="w-8 h-8 text-primary-500" />
    return <FileSpreadsheet className="w-8 h-8 text-green-500" />
  }

  const submitImport = async ({ validateOnly: dryRun, confirmReplace: confirm = false }) => {
    if (!file) return

    setIsUploading(true)
    validateOnlyRef.current = dryRun

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('mode', mode)
      formData.append('validateOnly', String(dryRun))
      if (mode === 'sync') {
        formData.append('confirmReplace', String(confirm))
      }

      const response = await fetch('/api/admin/questions/import', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${JSON.parse(localStorage.getItem('auth-storage'))?.state?.token}`
        },
        body: formData
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed')
      }

      setImportJob({
        id: data.importId,
        status: 'PENDING',
        totalRecords: data.totalRecords,
        processedRecords: 0,
        successCount: 0,
        errorCount: 0
      })

      // Start polling for updates
      startPolling(data.importId)

      toast.success(dryRun ? 'Preview started' : 'Import job started')
    } catch (error) {
      logger.error('Import error:', error)
      toast.error(error.message || 'Failed to start import')
      setIsUploading(false)
    }
  }

  // Pseudo-replace runs a mandatory dry-run preview first, then applies on confirmation.
  const isSync = mode === 'sync'
  const syncPreview = importJob?.metadata?.syncPreview // present on a completed sync dry-run
  const syncResult = importJob?.metadata?.syncResult // present on a completed real sync

  const handlePrimary = () => {
    if (isSync) {
      // First step for sync is always a preview (dry run); the real apply is a separate button.
      submitImport({ validateOnly: true, confirmReplace: false })
    } else {
      submitImport({ validateOnly, confirmReplace: false })
    }
  }

  const handleApplySync = () => {
    submitImport({ validateOnly: false, confirmReplace })
  }

  const handleDownloadTemplate = async (format) => {
    try {
      window.open(`/api/admin/questions/import/template?format=${format}`, '_blank')
    } catch (error) {
      toast.error('Failed to download template')
    }
  }

  const handleRetry = async () => {
    if (!importJob?.id) return

    try {
      const response = await api.post(`/admin/questions/import/${importJob.id}/retry`)
      const { newImportId } = response.data
      setImportJob({
        id: newImportId,
        status: 'PENDING',
        totalRecords: importJob.totalRecords,
        processedRecords: 0,
        successCount: 0,
        errorCount: 0
      })
      // Start polling for updates
      startPolling(newImportId)
      toast.success('Retry started')
    } catch (error) {
      toast.error(error.message || 'Failed to retry import')
    }
  }

  const handleClose = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    importIdRef.current = null
    setFile(null)
    setImportJob(null)
    setIsUploading(false)
    setConfirmReplace(false)
    onClose()
  }

  if (!isOpen) return null

  const progress =
    importJob?.totalRecords > 0
      ? Math.round((importJob.processedRecords / importJob.totalRecords) * 100)
      : 0

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-500'
      case 'FAILED':
        return 'text-red-500'
      case 'CANCELLED':
        return 'text-gray-500'
      default:
        return 'text-primary-500'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'FAILED':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      default:
        return <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-10">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Import Questions</h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {!importJob ? (
            <>
              {/* Download Template */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-sm text-gray-600 dark:text-gray-300">Need a template?</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownloadTemplate('json')}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded hover:bg-primary-100 dark:hover:bg-primary-900/50"
                  >
                    <Download className="w-3 h-3" />
                    JSON
                  </button>
                  <button
                    onClick={() => handleDownloadTemplate('csv')}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 rounded hover:bg-green-100 dark:hover:bg-green-900/50"
                  >
                    <Download className="w-3 h-3" />
                    CSV
                  </button>
                </div>
              </div>

              {/* File Upload */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  isDragging
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                    : file
                      ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/30'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    {getFileIcon(file.name)}
                    <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setFile(null)
                      }}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                    <p className="font-medium text-gray-700 dark:text-gray-300">
                      Drop a file here or click to upload
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Supports JSON and CSV files
                    </p>
                  </div>
                )}
              </div>

              {/* Import Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Import Mode
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="radio"
                      name="mode"
                      value="upsert"
                      checked={mode === 'upsert'}
                      onChange={(e) => setMode(e.target.value)}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Upsert</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Update existing questions (by ID), insert new ones
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="radio"
                      name="mode"
                      value="insert_only"
                      checked={mode === 'insert_only'}
                      onChange={(e) => setMode(e.target.value)}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Insert Only</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Skip questions with existing IDs, only insert new ones
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="radio"
                      name="mode"
                      value="sync"
                      checked={mode === 'sync'}
                      onChange={(e) => setMode(e.target.value)}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Pseudo-replace (authoritative)
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Upsert the file, then move any active question of the same test type(s) that
                        is NOT in the file to review. Non-destructive and reversible (auto-restores
                        if re-imported). You will preview the changes before applying.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Validate Only Checkbox (not shown for pseudo-replace, which always previews first) */}
              {!isSync && (
                <label className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={validateOnly}
                    onChange={(e) => setValidateOnly(e.target.checked)}
                    className="rounded text-yellow-600"
                  />
                  <div>
                    <p className="font-medium text-yellow-800 dark:text-yellow-300">
                      Validate Only (Dry Run)
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400">
                      Check for errors without importing
                    </p>
                  </div>
                </label>
              )}
            </>
          ) : (
            <>
              {/* Import Progress */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {getStatusIcon(importJob.status)}
                  <span className={`font-medium ${getStatusColor(importJob.status)}`}>
                    {importJob.status === 'COMPLETED'
                      ? validateOnly
                        ? 'Validation Complete'
                        : 'Import Complete'
                      : importJob.status === 'FAILED'
                        ? 'Import Failed'
                        : importJob.status === 'VALIDATING'
                          ? 'Validating...'
                          : importJob.status === 'PROCESSING'
                            ? 'Processing...'
                            : 'Starting...'}
                  </span>
                </div>

                {/* Progress Bar */}
                {['PENDING', 'VALIDATING', 'PROCESSING'].includes(importJob.status) && (
                  <div className="space-y-1">
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                      {importJob.processedRecords?.toLocaleString() || 0} /{' '}
                      {importJob.totalRecords?.toLocaleString() || 0} records
                    </p>
                  </div>
                )}

                {/* Success Info */}
                {importJob.status === 'COMPLETED' && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {importJob.successCount?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300">Imported</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                          {importJob.errorCount?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300">Skipped</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                          {importJob.totalRecords?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Total</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pseudo-replace: dry-run preview (before applying) */}
                {importJob.status === 'COMPLETED' && syncPreview && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg space-y-2">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      Pseudo-replace preview ({syncPreview.testTypes?.join(', ') || 'no test types'}
                      )
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div>
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                          {syncPreview.flaggedCount?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          Will move to review
                        </p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {syncPreview.restoredCount?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300">Will restore</p>
                      </div>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      Flagged questions are withheld from students but stay visible in the grid
                      (&quot;In review&quot;) and auto-restore if re-imported. Nothing is deleted.
                    </p>
                    {syncPreview.flaggedCount > 0 && (
                      <label className="flex items-center gap-2 pt-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmReplace}
                          onChange={(e) => setConfirmReplace(e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-sm text-blue-800 dark:text-blue-300">
                          I understand {syncPreview.flaggedCount?.toLocaleString()} question(s) will
                          be moved to review.
                        </span>
                      </label>
                    )}
                  </div>
                )}

                {/* Pseudo-replace: applied result */}
                {importJob.status === 'COMPLETED' && syncResult && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                    <p className="text-sm text-green-800 dark:text-green-300">
                      Pseudo-replace applied: {syncResult.flaggedCount?.toLocaleString() || 0} moved
                      to review, {syncResult.restoredCount?.toLocaleString() || 0} restored (
                      {syncResult.testTypes?.join(', ')}).
                    </p>
                  </div>
                )}

                {/* Errors */}
                {importJob.status === 'FAILED' && importJob.errors && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg space-y-2 max-h-48 overflow-y-auto">
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">Errors:</p>
                    {importJob.errors.slice(0, 10).map((error, idx) => (
                      <div key={idx} className="text-sm text-red-600 dark:text-red-400">
                        <span className="font-medium">Row {error.row || idx + 1}:</span>{' '}
                        {error.errors?.join(', ') || error.message}
                      </div>
                    ))}
                    {importJob.errors.length > 10 && (
                      <p className="text-sm text-red-500 dark:text-red-400 italic">
                        ...and {importJob.errors.length - 10} more errors
                      </p>
                    )}
                  </div>
                )}

                {/* Warnings (from metadata) */}
                {importJob.status === 'COMPLETED' && importJob.metadata?.warnings?.length > 0 && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg space-y-2 max-h-32 overflow-y-auto">
                    <div className="flex items-center gap-1 text-sm font-medium text-yellow-700 dark:text-yellow-300">
                      <AlertTriangle className="w-4 h-4" />
                      Warnings:
                    </div>
                    {importJob.metadata.warnings.slice(0, 5).map((warning, idx) => (
                      <div key={idx} className="text-sm text-yellow-600 dark:text-yellow-400">
                        Row {warning.row}: {warning.warnings?.join(', ')}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            {importJob?.status === 'COMPLETED' ? 'Close' : 'Cancel'}
          </button>

          {!importJob ? (
            <button
              onClick={handlePrimary}
              disabled={!file || isUploading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {isSync ? 'Preview changes' : validateOnly ? 'Validate' : 'Start Import'}
            </button>
          ) : importJob.status === 'FAILED' ? (
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          ) : importJob.status === 'COMPLETED' && syncPreview ? (
            <button
              onClick={handleApplySync}
              disabled={isUploading || (syncPreview.flaggedCount > 0 && !confirmReplace)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Apply pseudo-replace
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default QuestionImportModal
